import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background-50/95 backdrop-blur-sm border-b border-background-200/70">
      <div className="flex items-center justify-between px-4 md:px-12 h-16 md:h-18">
        <Link
          to="/"
          className="flex items-center gap-2 text-foreground-950 hover:text-primary-600 transition-colors cursor-pointer"
        >
          <i className="ri-database-2-line text-xl md:text-2xl w-6 h-6 flex items-center justify-center"></i>
          <span className="font-heading text-base md:text-lg font-semibold whitespace-nowrap">
            OOPBuy Spreadsheet
          </span>
        </Link>

        <div className="hidden md:flex items-center gap-6">
          <Link
            to="/"
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Home
          </Link>

          <Link
            to="/article/oopbuy-spreadsheet-beginners-guide"
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Shopping Guide
          </Link>

          <div ref={dropdownRef} className="relative">
            <button
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex items-center gap-1 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              Categories
              <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {dropdownOpen && (
              <div className="absolute top-full left-0 mt-2 w-64 bg-background-50 border border-background-200/70 rounded-lg shadow-lg py-2 max-h-80 overflow-y-auto z-50">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-background-100 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-8 h-8 rounded-md object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <span className="whitespace-nowrap">{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            oopbuy spreadsheet
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-5 py-2 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            Buy It Now
            <i className="ri-arrow-right-up-line w-4 h-4 flex items-center justify-center"></i>
          </a>
        </div>

        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden w-10 h-10 flex items-center justify-center text-foreground-700 cursor-pointer"
        >
          <i className={`text-xl ${mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
        </button>
      </div>

      {mobileMenuOpen && (
        <div className="md:hidden bg-background-50 border-t border-background-200/70 px-4 py-3 flex flex-col gap-1 max-h-[70vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 py-3 px-2 text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer rounded-lg"
          >
            <i className="ri-home-4-line w-5 h-5 flex items-center justify-center"></i>
            Home
          </Link>

          <Link
            to="/article/oopbuy-spreadsheet-beginners-guide"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 py-3 px-2 text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer rounded-lg"
          >
            <i className="ri-book-open-line w-5 h-5 flex items-center justify-center"></i>
            Shopping Guide
          </Link>

          <div className="border-t border-background-200/70 pt-2 mt-1">
            <button
              onClick={() => setMobileCatOpen(!mobileCatOpen)}
              className="flex items-center justify-between w-full py-3 px-2 text-sm font-semibold text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer rounded-lg"
            >
              <span className="flex items-center gap-3">
                <i className="ri-grid-line w-5 h-5 flex items-center justify-center"></i>
                Categories
              </span>
              <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${mobileCatOpen ? 'rotate-180' : ''}`}></i>
            </button>

            {mobileCatOpen && (
              <div className="grid grid-cols-2 gap-1.5 px-2 pb-2">
                {categories.map((cat) => (
                  <a
                    key={cat.slug}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    onClick={() => setMobileMenuOpen(false)}
                    className="flex items-center gap-2 py-2 px-2 text-xs text-foreground-700 hover:bg-background-100 hover:text-primary-600 rounded-lg transition-colors cursor-pointer"
                  >
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-7 h-7 rounded object-cover flex-shrink-0"
                      loading="lazy"
                    />
                    <span>{cat.name}</span>
                  </a>
                ))}
              </div>
            )}
          </div>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center gap-3 py-3 px-2 text-sm font-medium text-foreground-700 hover:text-primary-600 transition-colors cursor-pointer rounded-lg"
          >
            <i className="ri-external-link-line w-5 h-5 flex items-center justify-center"></i>
            oopbuy spreadsheet
          </a>

          <a
            href="https://kaspreadsheet.com/"
            target="_blank"
            rel="noopener nofollow"
            onClick={() => setMobileMenuOpen(false)}
            className="flex items-center justify-center gap-2 mt-1 py-2.5 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
          >
            <i className="ri-shopping-bag-3-line w-4 h-4 flex items-center justify-center"></i>
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}